import React, { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import DatePicker from 'react-datepicker';
import DatePickerInput from '../DatePickerInput';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import './styles.scss';

export default class WrappedDatePicker extends Component {
  static propTypes = {
    isFinalExam: PropTypes.bool,
    input: PropTypes.shape({
      onChange: PropTypes.func,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }).isRequired,
    meta: PropTypes.shape({
      touched: PropTypes.bool,
      error: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    }).isRequired,
    inputValueFormat: PropTypes.string,
    hasReset: PropTypes.bool,
    timezone: PropTypes.string.isRequired,
    isNewModal: PropTypes.bool,
    isAssign: PropTypes.bool,
    isAssignmentTable: PropTypes.bool,
    hideTodayButton: PropTypes.bool,
    inline: PropTypes.bool,
    onPastSelect: PropTypes.func,
    onTimeSelect: PropTypes.func,
  };

  static defaultProps = {
    isFinalExam: false,
    inputValueFormat: null,
    dateFormat: 'MM/dd/yyyy hh:mm a',
    minDate: dayjs().valueOf(),
    maxDate: '',
    timeFormat: 'hh:mm a',
    timeIntervals: 15,
    showTimeSelect: true,
    dropdownMode: 'select',
    placeholderText: 'MM/DD/YYYY',
    selectedValue: null,
    utcOffset: 0,
    hasReset: false,
    isNewModal: false,
    isAssign: false,
    hideTodayButton: false,
    inline: false,
    isAssignmentTable: false,
    onPastSelect: () => {},
    onTimeSelect: () => {},
  };

  constructor(props) {
    super(props);

    this.state = {
      timeoutId: null,
      intervalId: null,
      useTimeNow: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.reset = this.reset.bind(this);
    this.onManualTimeSet = this.onManualTimeSet.bind(this);
    this.timeRef = createRef();
  }

  componentDidMount() {
    /**
     * ensure when the modal loads we start making sure the StartDate
     * is not in the past
     */
    const { isAssign, isNewModal } = this.props;
    isAssign && isNewModal && this.createTimeout();
  }

  componentDidUpdate(prevProps, prevState) {
    const { useTimeNow } = this.state;
    const { useTimeNow: prevUseTimeNow } = prevState;
    const { isAssign, isNewModal } = this.props;

    if (!isAssign && isNewModal) {
      if (!prevUseTimeNow && useTimeNow) {
        this.createTimeout();
      } else if (prevUseTimeNow && !useTimeNow) {
        this.clearIntervals();
      }
    }
  }

  componentWillUnmount() {
    /**
     * cleanup timeout and interval logic
     */
    this.clearIntervals();
  }

  onManualTimeSet({ hours, minutes }) {
    const { selectedValue, isFinalExam } = this.props;

    if (selectedValue) {
      let date = dayjs(selectedValue, 'YYYY-MM-DDTHH:mm:ss');
      date = date.set('hour', hours);
      date = date.set('minute', minutes);
      if (!isFinalExam) {
        date = date.set('second', 0);
        date = date.utcOffset(0, true);
      }

      this.handleChange(date);
    }
  }

  getOffsetDate = (date) => {
    const { timezone } = this.props;
    const tzDate = (date && dayjs.tz(date, timezone)) || dayjs().tz(timezone);
    const offSet = tzDate.utcOffset();
    return tzDate.utcOffset(offSet, true);
  };

  setToNow = () => {
    const { input, isAssign } = this.props;

    input.onChange(this.getOffsetDate());
    this.timeRef.current.setOpen(false);

    !isAssign &&
      this.setState({
        useTimeNow: true,
      });
  };

  createTimeout = (selectedDate) => {
    const {
      input: { name, value },
      timezone,
    } = this.props;
    if (name !== 'startsAt') return;
    this.clearIntervals();
    const now = dayjs().tz(timezone);
    const date = selectedDate || value || now;
    const milliSecondMultiplier = 1000;
    const diff = date.diff(now) / milliSecondMultiplier;
    const aMinute = 60;
    const timeoutValue = diff <= aMinute ? 0 : diff * milliSecondMultiplier;
    // at the point when the start date time is equal to now, start incrementing by 1 minute
    const timeout = setTimeout(() => {
      /**
       * check every ten seconds to make it less likely that user submits modal with
       * StartDate in the past
       */
      const interval = setInterval(
        this.updateStartTime,
        10 * milliSecondMultiplier,
      );
      this.setState({ intervalId: interval });
    }, timeoutValue);
    this.setState({ timeoutId: timeout });
  };

  /**
   * cleanup timeout and interval
   */
  clearIntervals = () => {
    const { timeoutId, intervalId } = this.state;
    clearInterval(intervalId);
    clearTimeout(timeoutId);
  };

  /**
   * actually sets the DatePicker input component to the updated value
   */
  updateStartTime = () => {
    const {
      input: { value },
      timezone,
    } = this.props;
    const now = dayjs().tz(timezone);
    /**
     * we do dayjs(value.toDate()) because the moment _i value is not updated so
     * value ended up having the second-to-last-time we picked on the datepicker while
     * value.toDate() returned the latest date we chose. subtle bug I found in a GH issue
     * that I cannot find anymore
     */
    if (value && value.startOf('minute').isBefore(dayjs().startOf('minute'))) {
      this.updateInput(dayjs(now).startOf('minute'));
    }
  };

  handleChange(rawDate, event) {
    const {
      input,
      minDate,
      maxDate,
      isAssign,
      isAssignmentTable,
      onPastSelect,
      timezone,
    } = this.props;
    let date = rawDate instanceof dayjs ? rawDate : dayjs(rawDate);
    const clonedDate = date.clone(); // use cloned date in below IFs to not alter the value of `date`
    if (timezone) {
      const dateInTimezoneOffset = clonedDate.tz(timezone).utcOffset(); // use timezone's offset instead of local
      date = date.utcOffset(dateInTimezoneOffset, true);
    }

    let newSelectedDate = null;

    if (
      minDate &&
      clonedDate.utcOffset(minDate.utcOffset(), true).isBefore(minDate)
    ) {
      if (isAssignmentTable) {
        onPastSelect();
        return;
      }
      newSelectedDate = minDate.clone();
    } else if (
      maxDate &&
      maxDate.isBefore(clonedDate.utcOffset(maxDate.utcOffset(), true))
    ) {
      newSelectedDate = maxDate.clone().subtract(1, 'minute');
    }

    newSelectedDate = (newSelectedDate && dayjs(newSelectedDate)) || date;

    if (!isAssign && input.name === 'startsAt') {
      this.setState(
        {
          useTimeNow: true,
        },
        () => {
          this.updateInput(newSelectedDate);
        },
      );
    } else {
      this.updateInput(newSelectedDate);
    }
    // We explicitly call the onTimeSelect() to close the Date Picker IF the Time section is selected
    const dayElementSelected = event?.target?.className;
    if (
      !dayElementSelected &&
      !dayElementSelected?.includes('react-datepicker__day')
    ) {
      this.props.onTimeSelect && this.props.onTimeSelect();
    }
  }

  updateInput = (date) => {
    const { input } = this.props;
    input.onChange(date);
  };

  reset() {
    const { input, isAssign } = this.props;
    input.onChange(null);
    !isAssign &&
      this.setState({
        useTimeNow: false,
      });
  }

  render() {
    const {
      meta: { touched, error },
      selectedValue,
      isFinalExam,
      hasReset,
      isNewModal,
      hideTodayButton,
      hasResetButton,
      inline,
      ...rest
    } = this.props;
    const customClass = rest.overridingProps.class;

    /*
     * If we will manually set the utcOffset it will make
     * react-datepicker flicker on first input.
     * So we have to set utc to be true, and add the needed
     * amount of hours manually, to display the time
     * as it is, ignoring the timezone.
     **/
    let dateValue = null;
    if (selectedValue) {
      dateValue = dayjs(selectedValue);
    }

    if (dateValue && !isFinalExam) {
      dateValue = dateValue.set('second', 0);
    }

    const withReset = hasReset && !!dateValue;

    /*
     * Need to exclude maxDate from props if it is empty string
     * to avoid annoying react-datepicker prop warnings.
     **/
    let overridingProps = rest;
    if (!overridingProps.maxDate) {
      overridingProps = _.omit(overridingProps, ['maxDate']);
    }

    const { minDate, maxDate, openToDate } = overridingProps;
    if (minDate) {
      overridingProps = {
        ...overridingProps,
        minDate: (minDate && minDate.valueOf()) || null,
      };
    }

    if (openToDate) {
      overridingProps = {
        ...overridingProps,
        openToDate: dateValue
          ? null
          : (openToDate && openToDate.valueOf()) || null,
      };
    }

    if (maxDate) {
      overridingProps = {
        ...overridingProps,
        maxDate: (maxDate && maxDate.valueOf()) || null,
      };
    }

    const openToDateFormatted = openToDate && openToDate.format('YYYY-MM-DD');
    const getDayClassName = (day) => {
      const isCurrentDay =
        dayjs(day).format('YYYY-MM-DD') === openToDateFormatted;

      return cn(
        {
          'hover:bg-blue-500 bg-blue-500 text-white': isCurrentDay,
        },
        'hover:rounded-md rounded-md',
      );
    };

    return (
      <div
        className={cn(
          'datepicker__outter-wrapper',
          touched && error ? 'has-error' : '',
        )}
      >
        <DatePicker
          customInput={
            <DatePickerInput
              customClass={customClass}
              onReset={withReset ? this.reset : null}
              onManualTimeSet={this.onManualTimeSet}
              hasResetButton={hasResetButton}
            />
          }
          selected={(dateValue && dateValue.valueOf()) || null}
          onChange={this.handleChange}
          ref={this.timeRef}
          {...overridingProps}
          disabledKeyboardNavigation
          dayClassName={!dateValue && openToDateFormatted && getDayClassName}
          popperModifiers={[
            {
              name: 'preventOverflow',
              options: {
                mainAxis: false,
              },
            },
          ]}
          inline={inline}
        >
          <Fragment>
            {isNewModal && !hideTodayButton && (
              <div
                className="datepicker__current-time"
                role="button"
                tabIndex="0"
                onClick={this.setToNow}
              >
                Select Current Date and Time
              </div>
            )}
          </Fragment>
        </DatePicker>
        {touched && error && <span className="datepicker__error">{error}</span>}
      </div>
    );
  }
}
